import classNames from '@/common/utils/classNames';
import { DsDrawer } from '@/modules/doctorHome/components/DsDrawer';
import { Check, ChevronLeft, MapPin } from 'lucide-react';
import { useState } from 'react';

export interface PanelCenterItem {
  id: string | number;
  name: string;
  address?: string | null;
}

/** «مرکز انتخاب شده» row (the same on every doctor-panel page); tap opens the list of centers in a bottom sheet. */
export const PanelCenterSelect = ({ centers, selectedId, onSelect }: { centers: PanelCenterItem[]; selectedId: string | number; onSelect: (id: string) => void }) => {
  const [open, setOpen] = useState(false);
  const active = centers.find(c => String(c.id) === String(selectedId));
  const canSwitch = centers.length > 1;
  return (
    <>
      <button
        type="button"
        disabled={!canSwitch}
        onClick={() => setOpen(true)}
        className="flex min-h-[64px] w-full items-center gap-3 rounded-2xl bg-white px-4 py-3 text-start transition-colors duration-150 active:bg-gray-50 disabled:active:bg-white"
      >
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
          <MapPin size={20} strokeWidth={1.8} aria-hidden />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block text-[12px] text-[#767c86]">مرکز انتخاب شده</span>
          <span className="line-clamp-2 block break-words text-[15px] font-medium leading-6 text-[#222]">{active?.name ?? 'انتخاب مرکز'}</span>
        </span>
        {canSwitch && <ChevronLeft size={20} className="shrink-0 text-[#b0b0b0]" aria-hidden />}
      </button>
      <DsDrawer open={open} onOpenChange={setOpen} title="انتخاب مرکز" level={1}>
        <div className="px-4 pb-4" dir="rtl">
          <h2 className="pb-3 pt-1 text-[18px] font-bold text-[#222]">انتخاب مرکز</h2>
          <ul>
            {centers.map(center => {
              const isActive = String(center.id) === String(selectedId);
              return (
                <li key={String(center.id)} className="border-b border-black/[0.06] last:border-0">
                  <button
                    type="button"
                    onClick={() => {
                      setOpen(false);
                      onSelect(String(center.id));
                    }}
                    className="flex min-h-[52px] w-full items-center justify-between gap-3 py-3 text-start transition-colors duration-150 active:bg-gray-50"
                  >
                    <span className="min-w-0 flex-1">
                      <span className={classNames('block break-words text-[15px]', isActive ? 'font-semibold text-primary' : 'font-medium text-[#222]')}>{center.name}</span>
                      {center.address ? <span className="mt-0.5 line-clamp-2 block text-[12.5px] text-[#767c86]">{center.address}</span> : null}
                    </span>
                    {isActive ? <Check size={20} className="shrink-0 text-primary" aria-hidden /> : null}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      </DsDrawer>
    </>
  );
};

/** First screen when no center is known: nelson's instruction bubble + a card per center. */
export const PanelCenterChooser = ({ instruction, centers, onSelect }: { instruction: string; centers: PanelCenterItem[]; onSelect: (id: string) => void }) => (
  <div className="flex flex-col gap-3">
    <p className="rounded-2xl bg-primary/10 px-4 py-3 text-center text-[13px] font-medium leading-6 text-primary">{instruction}</p>
    <h3 className="px-1 text-[13px] font-semibold text-primary">مراکز درمانی شما</h3>
    {centers.map(center => (
      <button
        key={String(center.id)}
        type="button"
        onClick={() => onSelect(String(center.id))}
        className="flex min-h-[64px] w-full items-center gap-3 rounded-2xl bg-white px-4 py-3.5 text-start transition-colors duration-150 active:bg-gray-50"
      >
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
          <MapPin size={20} strokeWidth={1.8} aria-hidden />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block break-words text-[15px] font-medium text-[#222]">{center.name}</span>
          {center.address ? <span className="mt-0.5 line-clamp-2 block text-[12.5px] text-[#767c86]">{center.address}</span> : null}
        </span>
        <ChevronLeft size={20} className="shrink-0 text-[#b0b0b0]" aria-hidden />
      </button>
    ))}
  </div>
);
